import {Component, effect, inject, input, Input, signal} from '@angular/core';
import {NgClass} from "@angular/common";
import {AnimalDetailsBarComponent} from "src/app/presentation/main-view/information-center/animals/animal-details-bar/animal-details-bar.component";
import {SpinnerComponent} from "src/app/presentation/common/spinner/spinner.component";
import {DataSubscriptionService} from "src/app/infrastructure/data-subscription.service";
import {StoppedMovingSvgComponent} from "src/app/presentation/common/icons/stopped-moving-svg/stopped-moving-svg.component";
import {OopSvgComponent} from "src/app/presentation/common/icons/oop-svg/oop-svg.component";
import {AbnormalActivitySvgComponent} from "src/app/presentation/common/icons/abnormal-activity-svg/abnormal-activity-svg.component";
import {AnimalStatusBarComponent} from "src/app/presentation/main-view/information-center/animals/animal-status-bar/animal-status-bar.component";
import {Animal, SynchronizationStatus} from "src/app/infrastructure/moovement-api/models/asset";
import {Samples} from "src/app/infrastructure/moovement-api/models/samples";
import { AggregatedAssetStateStore } from 'src/app/domain/assets/aggregated-state/aggregated-asset-state.store';
import { Router } from '@angular/router';
import { AnimalsStore } from 'src/app/domain/assets/animals/animals.store';
import { AnimalProfilePictureComponent } from "src/app/presentation/common/icons/animal-profile-picture/animal-profile-picture.component";
import { AnimalSelectionStore } from 'src/app/domain/assets/animals/animal-selection.store';
import { UserStore } from 'src/app/domain/user/user.store';
import NfcLocationSampleMetadata = Samples.NfcLocationSampleMetadata;
import {NfcLocationMetadataStore} from "src/app/domain/samples/nfc-location/nfc-location.store";

@Component({
  selector: 'app-animal-button',
  imports: [
    AnimalDetailsBarComponent,
    SpinnerComponent,
    StoppedMovingSvgComponent,
    OopSvgComponent,
    AbnormalActivitySvgComponent,
    AnimalStatusBarComponent,
    NgClass,
    AnimalProfilePictureComponent
],
  templateUrl: './animal-button.component.html',
  styleUrl: './animal-button.component.css'
})
export class AnimalButtonComponent {

  @Input() clickable: boolean = true;
  @Input() selected: boolean = false;
  @Input() selectableForAlteration?: boolean;
  @Input() selectableForDeletion?: boolean;
  standalone = input.required<boolean>()
  id = input<string>();
  observingDeviceId = input<string>();

  animal = signal<Animal | undefined>(undefined);
  oop = signal(false);
  stoppedMoving = signal(false);
  abnormalActivity = signal(false);
  abnormalActivityIsHigh = signal(false);
  loading = signal(true);

  readonly SynchronizationStatus = SynchronizationStatus;

  private animalSelectionStore = inject(AnimalSelectionStore);
  private aggregatedAssetStateStore = inject(AggregatedAssetStateStore);
  private animalsStore = inject(AnimalsStore);
  private router = inject(Router);
  private userStore = inject(UserStore);

  constructor(
    protected dataSubscriptionService: DataSubscriptionService
  ) {
    effect(() => {
      const assetId = this.id();
      if (!assetId) return;

      const aggregatedAnimalState = this.aggregatedAssetStateStore.entityMap()[assetId];
      if (!aggregatedAnimalState) return;

      this.oop.set(aggregatedAnimalState.outOfFarmSince !== undefined || aggregatedAnimalState.outOfPaddockSince !== undefined)
      this.stoppedMoving.set(aggregatedAnimalState.stoppedMovingSince !== undefined);
      this.abnormalActivity.set(aggregatedAnimalState.abnormalActivitySince !== undefined);
      this.abnormalActivityIsHigh.set(aggregatedAnimalState.abnormalActivityStatus === Samples.AlertStatus.ABNORMALLY_HIGH_ACTIVITY);
    });

    effect(() => {
      const assetId = this.id();
      if (!assetId) return;

      const animal = this.animalsStore.entityMap()[assetId];
      this.animal.set(animal);
      this.loading.set(false);
    });

    effect(() => {
      if (this.selectableForAlteration == undefined) {
        this.selectableForAlteration = this.userStore.canEditPersistable(this.animal());
      }
      if (this.selectableForDeletion == undefined) {
        this.selectableForDeletion = this.userStore.canDeletePersistable(this.animal());
      }
    });

  }

  focus() {
    this.router.navigate(['/main/animal-details', this.id()]);
  }

  onCheckUncheck(event: Event) {
    event.stopPropagation();

    const animal = this.animal();
    if (!animal) return;

    this.selected = !this.selected;
    if (this.selected) {
      this.animalSelectionStore.addEntity(animal);
    } else {
      this.animalSelectionStore.removeEntity(animal.id);
    }
  }
}
